import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, TrendingUp, Droplet, Clock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

const Home = () => {
  const services = [
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Restnutzungsdauer',
      description: 'Steuerlich wirksame Gutachten zur realistischen Bewertung und Optimierung der Abschreibung.',
      path: '/restnutzungsdauer',
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: 'Gutachten',
      description: 'Klare Analysen, die Risiken einordnen und Entscheidungen ermöglichen.',
      path: '/gutachten',
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Investments',
      description: 'Fundierte Begleitung von Immobilienentscheidungen aus Entwickler- und Investorensicht.',
      path: '/investments',
    },
    {
      icon: <Droplet className="w-8 h-8" />,
      title: 'Spezialgutachten',
      description: 'Feuchte- und Nässegutachten mit konkreten, umsetzbaren Sanierungskonzepten.',
      path: '/spezialgutachten',
    },
  ];

  const stats = [
    { label: 'Jahre Erfahrung', value: '40+' },
    { label: 'Realisierte Projekte', value: '500+' },
    { 
      label: '100 Mio. €', 
      value: 'Beratung und Strukturierung von Vermögensstrukturen im Family-Office-Umfeld mit einem Volumen von über 100 Mio. €',
      isLongText: true 
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="flex flex-col animate-fade-in">
              <div className="space-y-6">
              <h1 className="text-5xl md:text-6xl font-bold text-brand-900 leading-tight">
                Substanz erkennen.{' '}
                <span className="text-brand-700">Werte sichern.</span>{' '}
                <span className="text-brand-800">Entscheidungen treffen.</span>
              </h1>
              <p className="text-xl text-brand-700 leading-relaxed">
                Immobilien verlangen mehr als Einschätzung. Sie verlangen Verständnis.
              </p>
              </div>
              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <Link to="/kontakt">
                  <Button 
                    size="lg" 
                    className="bg-brand-900 hover:bg-brand-800 text-white px-8 py-3 text-base transition-all duration-300 hover:shadow-lg"
                  >
                    Kontakt aufnehmen
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/gutachten">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-brand-300 hover:border-brand-500 text-brand-800 px-8 py-3 text-base transition-all duration-300"
                  >
                    Leistungen entdecken
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1773085266769-fbee873610bd"
                alt="Moderne Architektur"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-900/20 to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-brand-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={index} 
                className="text-center p-8 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                {stat.isLongText ? (
                  <>
                    <div className="text-brand-900 font-bold text-lg mb-3">
                      {stat.label}
                    </div>
                    <div className="text-brand-700 text-base leading-relaxed">
                      {stat.value}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-4xl md:text-5xl font-bold text-brand-900 mb-2">
                      {stat.value}
                    </div>
                    <div className="text-brand-700 font-medium">{stat.label}</div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-brand-900 mb-4">
              Unsere Leistungsbereiche
            </h2>
            <p className="text-xl text-brand-700 max-w-3xl mx-auto">
              Präzise Analysen und fundierte Entscheidungsgrundlagen für Ihre Immobilien
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <Link 
                key={index} 
                to={service.path}
                className="group"
              >
                <Card className="h-full border-2 border-brand-200 hover:border-brand-500 transition-all duration-300 hover:shadow-xl cursor-pointer">
                  <CardHeader className="p-8">
                    <div className="text-brand-800 mb-4 group-hover:text-brand-900 transition-colors">
                      {service.icon}
                    </div>
                    <CardTitle className="text-2xl mb-3 group-hover:text-brand-900 transition-colors">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-base text-brand-700 leading-relaxed">
                      {service.description}
                    </CardDescription>
                    <div className="flex items-center mt-4 text-brand-800 font-medium group-hover:text-brand-900 transition-colors">
                      Mehr erfahren
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardHeader>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Vermögensstrukturierung Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-900 mb-6">
              Vermögensstrukturierung
            </h2>
            <p className="text-xl text-brand-800 max-w-3xl mx-auto leading-relaxed">
              Wir strukturieren Vermögen dort, wo Standardlösungen nicht mehr ausreichen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            <div className="p-6 bg-white rounded-xl border-2 border-brand-200 hover:border-brand-400 transition-all duration-300 hover:shadow-lg">
              <h3 className="text-xl font-bold text-brand-900 mb-3">Holdingstrukturen</h3>
              <p className="text-brand-700 leading-relaxed">
                Konzeption und Optimierung steuerlich effizienter Beteiligungsstrukturen für Unternehmer und Investoren.
              </p>
            </div>

            <div className="p-6 bg-white rounded-xl border-2 border-brand-200 hover:border-brand-400 transition-all duration-300 hover:shadow-lg">
              <h3 className="text-xl font-bold text-brand-900 mb-3">Immobilienstrukturen</h3>
              <p className="text-brand-700 leading-relaxed">
                Aufbau und Strukturierung von Immobilienportfolios, Projektgesellschaften und Exit-Strukturen.
              </p>
            </div>

            <div className="p-6 bg-white rounded-xl border-2 border-brand-200 hover:border-brand-400 transition-all duration-300 hover:shadow-lg">
              <h3 className="text-xl font-bold text-brand-900 mb-3">Private Investments</h3>
              <p className="text-brand-700 leading-relaxed">
                Begleitung bei Beteiligungen, Joint Ventures und individuellen Investmentstrukturen.
              </p>
            </div>

            <div className="p-6 bg-white rounded-xl border-2 border-brand-200 hover:border-brand-400 transition-all duration-300 hover:shadow-lg">
              <h3 className="text-xl font-bold text-brand-900 mb-3">Krypto & digitale Assets</h3>
              <p className="text-brand-700 leading-relaxed">
                Steuerliche und rechtliche Strukturierung von Krypto-Vermögen und digitalen Beteiligungsmodellen.
              </p>
            </div>

            <div className="p-6 bg-white rounded-xl border-2 border-brand-200 hover:border-brand-400 transition-all duration-300 hover:shadow-lg md:col-span-2 lg:col-span-1">
              <h3 className="text-xl font-bold text-brand-900 mb-3">Nachfolge & Vermögensschutz</h3>
              <p className="text-brand-700 leading-relaxed">
                Entwicklung nachhaltiger Konzepte zur Sicherung und Übertragung von Vermögen.
              </p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-lg text-brand-800 font-medium">
              Unsere Beratung ist interdisziplinär und langfristig ausgerichtet.
            </p>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-brand-900 mb-8" translate="no">
            Was uns auszeichnet
          </h2>
          <p className="text-xl text-brand-800 leading-relaxed mb-8">
            Wir verbinden bautechnische Präzision, wirtschaftliches Denken und klare Handlungskompetenz. 
            Unsere Analysen enden nicht bei der Beschreibung – sie führen zu einer klaren Entscheidung.
          </p>
          <p className="text-xl text-brand-700 leading-relaxed">
            Das gibt Sicherheit und schafft Vertrauen.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center flex flex-col items-center gap-8">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-900">
            Wer mit uns arbeitet, will keine Unsicherheit.
          </h2>
          <p className="text-xl text-brand-700 max-w-3xl">
            Sondern eine belastbare Grundlage für richtige Entscheidungen.
          </p>
          <Link to="/kontakt">
            <Button 
              size="lg" 
              className="bg-brand-900 hover:bg-brand-800 text-white px-10 py-3 text-lg transition-all duration-300 hover:shadow-lg"
            >
              Jetzt Kontakt aufnehmen
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
